import { LayoutContext } from 'contexts/layoutContext/LayoutContext';
import { AgenciaCooperativa } from 'models/naoAssociado';
import {
  Button,
  Col,
  Collapse,
  Input,
  Row,
  SearchIcon,
} from 'plataforma-fundacao-componentes';
import { useCallback, useContext, useEffect, useState } from 'react';
import { getAgenciaPorCep } from 'services/Api';
import { getErrorMessages, showToastErrors } from 'utils/ErrorUtils';
import { languageValues } from 'utils/LanguageUtils';
import { formatCEP, getUnmaskedValue } from 'utils/MaskUtils';

const texts = languageValues.pages.naoAssociado;

type Props = {
  selecionarAgencia: (value?: string) => void;
  selecionarCooperativa: (value?: string) => void;
};

export function BuscarAgenciaPorCep({
  selecionarAgencia,
  selecionarCooperativa,
}: Props) {
  const { showToast } = useContext(LayoutContext);

  const [cep, setCep] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [cooperativaAgencia, setCooperativaAgencia] = useState<
    AgenciaCooperativa | undefined
  >();

  const buscar = useCallback(() => {
    (async () => {
      try {
        setIsLoading(true);
        const onlyNumbers = getUnmaskedValue(cep);
        const data = await getAgenciaPorCep(onlyNumbers);

        setCooperativaAgencia(data);
      } catch (error) {
        showToastErrors(showToast, getErrorMessages(error));
      } finally {
        setIsLoading(false);
      }
    })();
  }, [cep, showToast]);

  // Limpa a agência quando inicia a busca por um novo CEP
  useEffect(() => {
    setCooperativaAgencia(undefined);
  }, [cep]);

  useEffect(() => {
    selecionarAgencia(cooperativaAgencia?.agencia);
  }, [cooperativaAgencia?.agencia, selecionarAgencia]);

  useEffect(() => {
    selecionarCooperativa(cooperativaAgencia?.codigoCoop);
  }, [cooperativaAgencia?.codigoCoop, selecionarCooperativa]);

  return (
    <>
      <Row>
        <Col>
          <Input
            label={texts.input.cep.label}
            placeholder={texts.input.cep.placeholder}
            value={cep}
            onChange={(event) => {
              const formatted = formatCEP(event.target.value);
              setCep(formatted);
            }}
            loading={isLoading}
          />
        </Col>
      </Row>
      <Row>
        <Col end>
          <Button
            rightIcon={<SearchIcon />}
            onClick={(event) => {
              event.preventDefault();
              buscar();
            }}
            disabled={cep.length < 9}
            loading={isLoading}
          >
            Buscar
          </Button>
        </Col>
      </Row>
      <Collapse animateOpacity opened={Boolean(cooperativaAgencia)}>
        <Row>
          <Col xs={12} md={6}>
            <Input
              label={texts.input.cooperativa.label}
              value={`${cooperativaAgencia?.codigoCoop} - ${cooperativaAgencia?.nomeCoop}`}
              disabled
            />
          </Col>

          <Col xs={12} md={6}>
            <Input
              label={texts.input.agencia.label}
              value={`${cooperativaAgencia?.agencia} - ${cooperativaAgencia?.nomeAgencia}`}
              disabled
            />
          </Col>
        </Row>
      </Collapse>
    </>
  );
}
